import { useTranslation } from 'react-i18next'
import { AppShell } from '@/components/layout/AppShell'
import { PageHero } from '@/components/ui/PageHero'
import { TradingViewChart } from '@/components/charts/TradingViewChart'
import { TradingViewEmbed } from '@/components/widgets/TradingViewEmbed'
import { OrderTicket } from './OrderTicket'
import { SymbolInfoPanel } from './SymbolInfoPanel'
import { PositionsPanel } from './PositionsPanel'
import { WatchlistPanel } from './WatchlistPanel'
import { MobileTradeView } from './MobileTradeView'
import { DepositPage } from '@/features/wallet/DepositPage'
import { WithdrawPage } from '@/features/wallet/WithdrawPage'
import { SettingsPage } from '@/features/settings/SettingsPage'
import { CopyTradePage } from '@/features/copy/CopyTradePage'
import { AnalysisPage } from '@/features/analysis/AnalysisPage'
import { useNavigation } from '@/contexts/NavigationContext'
import { useMarket } from '@/contexts/MarketContext'
import { formatPercentChange } from '@/lib/format'
import { cn } from '@/lib/cn'

/**
 * Feature root: desktop multi-pane terminal + mobile/PWA route screens.
 */
export function TradeWorkspace() {
  const { t, i18n } = useTranslation(['trading', 'common'])
  const { route } = useNavigation()
  const { active } = useMarket()
  const up = active.changePct >= 0

  const chartPane = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-9 items-center justify-between gap-3 border-b border-terminal-border/40 px-3">
        <div className="flex min-w-0 items-baseline gap-2">
          <h1 className="truncate font-mono text-sm font-semibold tabular-nums">
            {active.label}
          </h1>
          <span
            className={cn(
              'rounded-sm px-1.5 py-0.5 font-mono text-[10px] tabular-nums',
              up ? 'bg-bid-badge text-bid' : 'bg-ask-badge text-ask',
            )}
          >
            {formatPercentChange(active.changePct)}
          </span>
        </div>
        <span className="shrink-0 font-mono text-[10px] text-terminal-muted">
          {active.tvSymbol}
        </span>
      </div>
      <div className="min-h-0 flex-1">
        <TradingViewChart
          symbol={active.tvSymbol}
          interval="15"
          locale={i18n.language}
        />
      </div>
    </div>
  )

  const ticket = (
    <div className="flex flex-col">
      <OrderTicket />
      <SymbolInfoPanel />
    </div>
  )
  const blotter = <PositionsPanel className="h-full border-0 rounded-none" />

  if (route === 'deposit') {
    return <AppShell main={<DepositPage />} mobile={<DepositPage />} />
  }

  if (route === 'withdraw') {
    return <AppShell main={<WithdrawPage />} mobile={<WithdrawPage />} />
  }

  if (route === 'economic_calendar') {
    const page = (
      <TradingViewEmbed
        kind="economic_calendar"
        tone="info"
        title={t('sidebar.economic_calendar', { ns: 'common' })}
        eyebrow={t('pages.calendarEyebrow', { ns: 'common' })}
        subtitle={t('pages.calendarSubtitle', { ns: 'common' })}
      />
    )
    return <AppShell main={page} mobile={page} />
  }

  if (route === 'news') {
    const page = (
      <TradingViewEmbed
        kind="timeline"
        tone="info"
        title={t('sidebar.news', { ns: 'common' })}
        eyebrow={t('pages.newsEyebrow', { ns: 'common' })}
        subtitle={t('pages.newsSubtitle', { ns: 'common' })}
      />
    )
    return <AppShell main={page} mobile={page} />
  }

  if (route === 'settings') {
    return <AppShell main={<SettingsPage />} mobile={<SettingsPage />} />
  }

  if (route === 'copy_trade') {
    return <AppShell main={<CopyTradePage />} mobile={<CopyTradePage />} />
  }

  if (route === 'analysis') {
    return <AppShell main={<AnalysisPage />} mobile={<AnalysisPage />} />
  }

  if (route === 'portfolio' || route === 'orders') {
    const isPortfolio = route === 'portfolio'
    const blotterPage = (
      <div className="flex h-full min-h-0 flex-col">
        <PageHero
          tone="info"
          glow="start"
          eyebrow={t(
            isPortfolio ? 'pages.portfolioEyebrow' : 'pages.ordersEyebrow',
            { ns: 'common' },
          )}
          title={t(isPortfolio ? 'nav.portfolio' : 'nav.orders', {
            ns: 'common',
          })}
          subtitle={t(
            isPortfolio
              ? 'pages.portfolioSubtitle'
              : 'pages.ordersSubtitle',
            { ns: 'common' },
          )}
        />
        <div className="min-h-0 flex-1">
          <PositionsPanel
            className="h-full border-0 rounded-none"
            initialTab={isPortfolio ? 'open' : 'pending'}
          />
        </div>
      </div>
    )
    return (
      <AppShell
        main={blotterPage}
        right={ticket}
        mobile={blotterPage}
      />
    )
  }

  if (route === 'markets') {
    return (
      <AppShell
        main={chartPane}
        right={ticket}
        bottom={blotter}
        mobile={
          <WatchlistPanel
            className="h-full"
            title={t('nav.markets', { ns: 'common' })}
            navigateOnSelect
          />
        }
      />
    )
  }

  return (
    <AppShell
      main={chartPane}
      right={ticket}
      bottom={blotter}
      mobile={<MobileTradeView />}
    />
  )
}
